import { useEffect, useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { CopyableCode } from '../../components/ui/CopyableCode';
import { EditIcon, ExternalLinkIcon, TruckIcon } from '../../components/ui/icons';
import { Markdown } from '../../components/ui/Markdown';
import { num } from '../../format';
import * as scanner from '../../platform/scanner';
import { useScanTarget } from '../../platform/scanning';
import { useAuth } from '../auth/AuthContext';
import { ComponentPicker } from '../components/ComponentPicker';
import type { ComponentBrief } from '../components/api';
import { useNavigation } from '../layout/navigation';
import { listProjects } from '../projects/api';
import { PrintDialog } from '../labels/PrintDialog';
import { LocationPicker } from '../locations/LocationPicker';
import { resolveScan } from '../scan/api';
import { SerialScanner } from '../serials/SerialScanner';
import { amountOf, priceOf } from './money';
import { PriceHint } from './PriceHint';
import { VendorCard, VendorForm } from './VendorForm';
import {
  addLine,
  archivePurchase,
  cancelPurchase,
  CHOOSABLE_STATUSES,
  deleteLine,
  getPurchase,
  listVendors,
  receivePurchase,
  setLineCoverage,
  updateLine,
  updatePurchase,
  type Purchase,
  type PurchaseLine,
  type PurchaseStatus,
  type ReceiveRequest,
  type ReceiptLine,
  type Vendor,
} from './api';
import { packageTypes as listPackageTypes } from '../stock/api';

interface Props {
  purchaseId: string;
  onArchived: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

const outstanding = (line: PurchaseLine) =>
  Number(line.quantity) - Number(line.received_quantity);

/**
 * One order: what was bought, where it is, and what has arrived.
 *
 * Ordering and receiving are kept apart on the screen as they are in the data.
 * A parcel takes weeks to come and is then unpacked into several boxes, so the
 * lines say what was ordered and the receiving form says what actually landed
 * and where — the two are never assumed to be the same.
 */
export function PurchasePanel({ purchaseId, onArchived, onDirtyChange }: Props) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigation = useNavigation();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [receiving, setReceiving] = useState(false);
  const [printingPackage, setPrintingPackage] = useState<string | null>(null);
  // null — not editing a vendor; a vendor — correcting that one; 'new' — adding.
  const [vendorForm, setVendorForm] = useState<Vendor | 'new' | null>(null);

  const canWrite = can('purchase.write');

  const order = useQuery({
    queryKey: ['purchases', purchaseId],
    queryFn: () => getPurchase(purchaseId),
  });
  const vendors = useQuery({ queryKey: ['reference', 'vendors'], queryFn: listVendors });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['purchases'] });
    void queryClient.invalidateQueries({ queryKey: ['stock'] });
    void queryClient.invalidateQueries({ queryKey: ['components'] });
  };
  const onFail = (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown');

  const patch = useMutation({
    mutationFn: (input: Parameters<typeof updatePurchase>[1]) =>
      updatePurchase(purchaseId, input),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: onFail,
  });

  const cancel = useMutation({
    mutationFn: () => cancelPurchase(purchaseId),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: onFail,
  });

  const drop = useMutation({
    mutationFn: () => archivePurchase(purchaseId),
    onSuccess: () => {
      refresh();
      onArchived();
    },
    onError: onFail,
  });

  const addRow = useMutation({
    mutationFn: (input: Parameters<typeof addLine>[1]) => addLine(purchaseId, input),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: onFail,
  });

  const editRow = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Parameters<typeof updateLine>[2] }) =>
      updateLine(purchaseId, id, input),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: onFail,
  });

  const dropRow = useMutation({
    mutationFn: (id: string) => deleteLine(purchaseId, id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: onFail,
  });

  const receive = useMutation({
    mutationFn: (input: ReceiveRequest) => receivePurchase(purchaseId, input),
    onSuccess: (received) => {
      setError(null);
      setReceiving(false);
      const firstPackage = received.lines.flatMap((line) => line.receipts).flatMap((item) => item.package_ids)[0];
      if (firstPackage && can('label.print')) setPrintingPackage(firstPackage);
      refresh();
    },
    onError: onFail,
  });

  if (order.isLoading) return <p className="panel__hint">{t('common.loading')}</p>;
  if (!order.data) return <p className="panel__error">{t('purchases.errors.unknown')}</p>;

  const purchase: Purchase = order.data;
  const open = purchase.status !== 'cancelled' && purchase.status !== 'received';
  const anythingReceived = purchase.lines.some((line) => Number(line.received_quantity) > 0);
  const vendor = vendors.data?.find((row) => row.id === purchase.vendor_id) ?? null;

  if (vendorForm) {
    return (
      <VendorForm
        vendor={vendorForm === 'new' ? null : vendorForm}
        onSaved={(saved) => {
          setVendorForm(null);
          // A shop added from inside an order is the shop that order came from.
          if (saved.id !== purchase.vendor_id) patch.mutate({ vendor_id: saved.id });
        }}
        onCancel={() => setVendorForm(null)}
      />
    );
  }

  return (
    // The same card as a component's, a place's or a project's. It was the one
    // record in the application whose detail was loose text on the page rather
    // than a card, and next to the others it read as a different application.
    <section className="panel purchase">
      <header className="panel__head">
        <div>
          <h2>{vendor?.name ?? t('purchases.noVendor')}</h2>
          {/* Code, state and date on the line under the title — the same shape a
              component's card uses for its code and part number. */}
          <p className="panel__sub">
            <CopyableCode
              value={purchase.code}
              className="components__code"
              label={t('purchases.code')}
            />
            <span className={`purchase__status purchase__status--${purchase.status}`}>
              {t(`purchases.status.${purchase.status}`)}
            </span>
            {purchase.ordered_at && <span>{purchase.ordered_at}</span>}
          </p>
        </div>

        {canWrite && (
          <div className="panel__actions">
            {navigation.canOpen('history') && (
              <button
                type="button"
                onClick={() => navigation.open('history', purchase.id, 'purchases')}
              >
                {t('audit.history')}
              </button>
            )}
            {open && (
              <button type="button" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
                {t('purchases.cancel')}
              </button>
            )}
            {/* Only for orders nothing arrived against: an order with receipts is
                the explanation for stock on the shelf, and hiding it would leave
                that stock unexplained. */}
            {!anythingReceived && (
              <button
                type="button"
                className="danger"
                onClick={() => drop.mutate()}
                disabled={drop.isPending}
              >
                {t('purchases.archive')}
              </button>
            )}
          </div>
        )}
      </header>

      {error && (
        <p className="panel__error">
          {t(`purchases.errors.${error}`, t('purchases.errors.unknown'))}
        </p>
      )}

      {/* Shown, not only editable. The form below belongs to whoever may change
          the order; the note — "for the October run, do not open before the
          audit" — is for everyone who opens it. */}
      {purchase.note && (
        <div className="panel__description">
          <Markdown source={purchase.note} lines={3} />
        </div>
      )}

      {/* The shop's details sit next to the order they explain: paying the
          invoice, chasing the parcel and ringing them all happen here. */}
      {vendor && (
        <VendorCard
          vendor={vendor}
          onEdit={canWrite ? () => setVendorForm(vendor) : undefined}
        />
      )}

      {canWrite && (
        <PurchaseHeaderForm
          purchase={purchase}
          vendors={(vendors.data ?? []).map((row) => ({ id: row.id, name: row.name }))}
          busy={patch.isPending}
          onSave={(input) => patch.mutate(input)}
          onAddVendor={() => setVendorForm('new')}
          onDirtyChange={onDirtyChange}
        />
      )}

      {/* Only for people who cannot open the form above: there the links sit
          under the fields they come from. Without this they would have no way
          to the parcel at all. */}
      {!canWrite && (purchase.order_url || purchase.tracking_url) && (
        <p className="purchase__links">
          {purchase.order_url && (
            <a href={purchase.order_url} target="_blank" rel="noreferrer">
              {t('purchases.openOrder')}
            </a>
          )}
          {purchase.tracking_url && (
            <a href={purchase.tracking_url} target="_blank" rel="noreferrer">
              {t('purchases.track')} {purchase.tracking_number}
            </a>
          )}
        </p>
      )}

      <div className="panel__section-head">
        <h3>{t('purchases.lines')}</h3>
      </div>
      {purchase.lines.length === 0 && <p className="panel__hint">{t('purchases.noLines')}</p>}

      <ul className="purchase__lines">
        {purchase.lines.map((line) => (
          <LineRow
            key={line.id}
            line={line}
            purchaseId={purchase.id}
            currency={purchase.currency}
            editable={canWrite}
            onSave={(input) => editRow.mutate({ id: line.id, input })}
            onDelete={() => dropRow.mutate(line.id)}
            onPrintPackage={can('label.print') ? setPrintingPackage : undefined}
            onCoverageChanged={refresh}
          />
        ))}
      </ul>

      {purchase.total_cost && (
        <p className="purchase__total">
          {t('purchases.total')}: <strong>{num(purchase.total_cost)}</strong>{' '}
          {purchase.currency}
        </p>
      )}

      {canWrite && open && (
        <AddLineForm busy={addRow.isPending} onAdd={(input) => addRow.mutate(input)} />
      )}

      {canWrite && open && purchase.lines.length > 0 && (
        <>
          <div className="panel__section-head">
            <h3>{t('purchases.receiving')}</h3>
          </div>
          {receiving ? (
            <ReceiveForm
              lines={purchase.lines}
              busy={receive.isPending}
              onSubmit={(lines) => receive.mutate(lines)}
              onCancel={() => setReceiving(false)}
            />
          ) : (
            <button type="button" onClick={() => setReceiving(true)}>
              {t('purchases.receive')}
            </button>
          )}
        </>
      )}

      {printingPackage && <PrintDialog entityKind="package" entityId={printingPackage} onClose={() => setPrintingPackage(null)} />}

    </section>
  );
}

function PurchaseHeaderForm({
  purchase,
  vendors,
  busy,
  onSave,
  onAddVendor,
  onDirtyChange,
}: {
  purchase: Purchase;
  vendors: { id: string; name: string }[];
  busy: boolean;
  onSave: (input: Parameters<typeof updatePurchase>[1]) => void;
  onAddVendor: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const { t } = useTranslation();
  const [vendorId, setVendorId] = useState(purchase.vendor_id ?? '');
  const [status, setStatus] = useState<PurchaseStatus>(purchase.status);
  const [orderedAt, setOrderedAt] = useState(purchase.ordered_at ?? '');
  const [expectedAt, setExpectedAt] = useState(purchase.expected_at ?? '');
  const [shipping, setShipping] = useState(num(purchase.shipping_cost));
  const [orderUrl, setOrderUrl] = useState(purchase.order_url ?? '');
  const [tracking, setTracking] = useState(purchase.tracking_number ?? '');
  const [note, setNote] = useState(purchase.note ?? '');

  const snapshot = JSON.stringify([
    purchase.vendor_id ?? '', purchase.status, purchase.ordered_at ?? '', purchase.expected_at ?? '', num(purchase.shipping_cost),
    purchase.order_url ?? '', purchase.tracking_number ?? '', purchase.note ?? '',
  ]);
  const current = JSON.stringify([vendorId, status, orderedAt, expectedAt, shipping, orderUrl, tracking, note]);
  const dirty = current !== snapshot;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  // Receiving changes the status in another form. Keep this editing form from
  // later sending its now-stale status back with harmless details such as a
  // tracking number or an order link.
  useEffect(() => { setStatus(purchase.status); }, [purchase.status]);

  // Statuses reached by receiving are shown but not offered: an order becomes
  // "received" because things arrived, not because somebody said so.
  const options: PurchaseStatus[] = CHOOSABLE_STATUSES.includes(purchase.status)
    ? CHOOSABLE_STATUSES
    : [purchase.status, ...CHOOSABLE_STATUSES];

  return (
    <form
      className="component-form"
      onSubmit={(event) => {
        event.preventDefault();
        onSave({
          vendor_id: vendorId || null,
          status,
          ordered_at: orderedAt || null,
          expected_at: expectedAt || null,
          shipping_cost: shipping || null,
          order_url: orderUrl || null,
          tracking_number: tracking || null,
          note: note || null,
        });
      }}
    >
      <div className="component-form__row">
        <label>
          {t('purchases.vendor')}
          <select value={vendorId} onChange={(event) => setVendorId(event.target.value)}>
            <option value="">{t('purchases.noVendor')}</option>
            {vendors.map((vendor) => (
              <option key={vendor.id} value={vendor.id}>
                {vendor.name}
              </option>
            ))}
          </select>
        </label>

        <label>
          {t('purchases.addVendor')}
          <button type="button" onClick={onAddVendor}>
            {t('purchases.addVendorShort')}
          </button>
        </label>

        <label>
          {t('purchases.statusLabel')}
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value as PurchaseStatus)}
          >
            {options.map((value) => (
              <option key={value} value={value}>
                {t(`purchases.status.${value}`)}
              </option>
            ))}
          </select>
        </label>

        <label>
          {t('purchases.orderedAt')}
          <input
            type="date"
            value={orderedAt}
            onChange={(event) => setOrderedAt(event.target.value)}
          />
        </label>
        <label>
          {t('purchases.expectedAt')}
          <input type="date" value={expectedAt} onChange={(event) => setExpectedAt(event.target.value)} />
        </label>
      </div>

      <div className="component-form__row">
        <label>
          {t('purchases.shipping')} ({purchase.currency})
          <input
            value={shipping}
            onChange={(event) => setShipping(event.target.value)}
            inputMode="decimal"
          />
        </label>

        {/* The link sits under the field it comes from rather than in a row of
            its own at the foot of the card: "track this" belongs next to the
            number being tracked, where the eye already is. */}
        {/* The way out sits against the field it comes from: a mark, because the
            field beside it already says what it is about. */}
        <label>
          {t('purchases.tracking')}
          <span className="field-with-link">
            <input value={tracking} onChange={(event) => setTracking(event.target.value)} />
            {purchase.tracking_url && (
              <a
                className="field-link"
                href={purchase.tracking_url}
                target="_blank"
                rel="noreferrer"
                title={`${t('purchases.track')} ${purchase.tracking_number ?? ''}`}
                aria-label={`${t('purchases.track')} ${purchase.tracking_number ?? ''}`}
              >
                <TruckIcon />
              </a>
            )}
          </span>
        </label>

        <label>
          {t('purchases.orderUrl')}
          <span className="field-with-link">
            <input
              type="url"
              value={orderUrl}
              onChange={(event) => setOrderUrl(event.target.value)}
              placeholder="https://"
            />
            {purchase.order_url && (
              <a
                className="field-link"
                href={purchase.order_url}
                target="_blank"
                rel="noreferrer"
                title={t('purchases.openOrder')}
                aria-label={t('purchases.openOrder')}
              >
                <ExternalLinkIcon />
              </a>
            )}
          </span>
        </label>
      </div>

      <label>
        {t('purchases.note')}
        <textarea value={note} onChange={(event) => setNote(event.target.value)} rows={3} />
        <span className="component-form__parsed">{t('common.markdownHint')}</span>
      </label>

      <div className="component-form__actions">
        <button type="submit" disabled={busy}>
          {t('common.save')}
        </button>
      </div>
    </form>
  );
}

function LineRow({
  line,
  purchaseId,
  currency,
  editable,
  onSave,
  onDelete,
  onPrintPackage,
  onCoverageChanged,
}: {
  line: PurchaseLine;
  purchaseId: string;
  currency: string;
  editable: boolean;
  onSave: (input: Parameters<typeof updateLine>[2]) => void;
  onDelete: () => void;
  onPrintPackage?: (packageId: string) => void;
  onCoverageChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const navigation = useNavigation();
  const [editing, setEditing] = useState(false);
  const [quantity, setQuantity] = useState(num(line.quantity));
  const [price, setPrice] = useState(num(line.unit_price));
  const [amount, setAmount] = useState(amountOf(line.quantity, line.unit_price ?? ''));
  const [purpose, setPurpose] = useState(line.purpose_note ?? '');
  const [expectedAt, setExpectedAt] = useState(line.expected_at ?? '');
  const [lotNumber, setLotNumber] = useState(line.receipt_lot_number ?? '');
  const [dateCode, setDateCode] = useState(line.receipt_date_code ?? '');
  const [packageType, setPackageType] = useState(line.receipt_package_type ?? '');
  const [tare, setTare] = useState(line.receipt_tare_weight_g ?? '');
  const [lotNote, setLotNote] = useState(line.receipt_lot_note ?? '');

  const left = outstanding(line);
  const sum = line.unit_price ? Number(line.quantity) * Number(line.unit_price) : null;

  if (editing) {
    return (
      <li className="purchase__line">
        <form
          className="purchase__line-form"
          onSubmit={(event) => {
            event.preventDefault();
            onSave({
              quantity,
              unit_price: price || null,
              purpose_note: purpose || null,
              expected_at: expectedAt || null,
              receipt_lot_number: lotNumber.trim() || null,
              receipt_date_code: dateCode.trim() || null,
              receipt_package_type: packageType.trim() || null,
              receipt_tare_weight_g: tare.trim() || null,
              receipt_lot_note: lotNote.trim() || null,
            });
            setEditing(false);
          }}
        >
          <div className="purchase__line-form-head">
            <span className="components__name">{line.component?.name}</span>
            <span className="purchase__line-actions">
              <button type="submit">{t('common.save')}</button>
              <button type="button" onClick={() => setEditing(false)}>
                {t('common.cancel')}
              </button>
            </span>
          </div>
          <div className="purchase__line-form-fields">
            <label>
              {t('purchases.quantity')}
              <input
                value={quantity}
                onChange={(event) => {
                  const next = event.target.value;
                  setQuantity(next);
                  // Whichever of price and amount was typed last is the figure the
                  // invoice states; the other one follows from the new quantity.
                  if (price) setAmount(amountOf(next, price));
                  else if (amount) setPrice(priceOf(next, amount));
                }}
                inputMode="decimal"
                size={6}
              />
            </label>
            <label>
              {t('purchases.amount')}
              <input
                value={amount}
                onChange={(event) => {
                  setAmount(event.target.value);
                  setPrice(priceOf(quantity, event.target.value));
                }}
                inputMode="decimal"
                size={8}
              />
            </label>
            <label>
              {t('purchases.price')}
              <input
                value={price}
                onChange={(event) => {
                  setPrice(event.target.value);
                  setAmount(amountOf(quantity, event.target.value));
                }}
                inputMode="decimal"
                size={7}
              />
            </label>
            <label>
              {t('purchases.purpose')}
              <input value={purpose} onChange={(event) => setPurpose(event.target.value)} />
            </label>
            <label>
              {t('purchases.expectedAt')}
              <input type="date" value={expectedAt} onChange={(event) => setExpectedAt(event.target.value)} />
            </label>
          </div>
          <ReceiptDefaultsFields
            lotNumber={lotNumber}
            dateCode={dateCode}
            packageType={packageType}
            tare={tare}
            note={lotNote}
            onLotNumber={setLotNumber}
            onDateCode={setDateCode}
            onPackageType={setPackageType}
            onTare={setTare}
            onNote={setLotNote}
            initiallyOpen={Boolean(lotNumber || dateCode || packageType || tare || lotNote)}
          />
        </form>
      </li>
    );
  }

  return (
    <li className="purchase__line">
      <span className="purchase__line-name">
        {/* The name is the way to the part. Reading an order and wanting to see
            what is left of something on it is the commonest move on this
            screen, and copying the code across by hand was the only way. */}
        {navigation.canOpen('component') ? (
          <button
            type="button"
            className="link components__name"
            title={t('purchases.openComponent')}
            onClick={() => navigation.open('component', line.component_id)}
          >
            {line.component?.name ?? line.component_id}
          </button>
        ) : (
          <span className="components__name">{line.component?.name ?? line.component_id}</span>
        )}
        <span className="components__meta">
          {line.component?.mpn && (
            <CopyableCode value={line.component.mpn} label={t('components.mpn')} />
          )}
          {line.component?.internal_code && (
            <CopyableCode
              value={line.component.internal_code}
              className="components__code"
              label={t('components.code')}
            />
          )}
          {line.purpose_note && <em>{line.purpose_note}</em>}
          {line.expected_at && <em>{t('purchases.expectedAt')}: {line.expected_at}</em>}
          {editable && <CoverageEditor purchaseId={purchaseId} line={line} onChanged={onCoverageChanged} />}
        </span>

        {/* Where it actually went, which is not the same as where the line said
            it should go: a parcel gets split, or the intended drawer is full.
            Shown per receipt rather than summed, because two drawers holding
            six and four is the fact somebody needs when they go looking. */}
        {line.receipts.length > 0 && (
          <span className="purchase__receipts">
            {line.receipts.map((receipt, index) => (
              /* The first row is the fact used to find stock; the quieter
                 second one records when it arrived and offers its label. */
              <span key={`${receipt.location_id}:${index}`} className="purchase__receipt">
                <span className="purchase__receipt-stock">
                  <span className="purchase__receipt-qty">{num(receipt.quantity)}</span>
                  {receipt.location_id && navigation.canOpen('location') ? (
                    <button
                      type="button"
                      className="link"
                      onClick={() => navigation.open('location', receipt.location_id as string)}
                    >
                      {receipt.location_name ?? receipt.location_code}
                    </button>
                  ) : (
                    <span>{receipt.location_name ?? t('purchases.outsideWarehouse')}</span>
                  )}
                </span>
                <span className="purchase__receipt-meta">
                  <em className="purchase__receipt-when">
                    {new Date(receipt.occurred_at).toLocaleDateString(i18n.language)}
                  </em>
                  {onPrintPackage && receipt.package_ids.map((packageId) => (
                    <button key={packageId} type="button" onClick={() => onPrintPackage(packageId)}>
                      {t('labels.print')}
                    </button>
                  ))}
                </span>
              </span>
            ))}
          </span>
        )}
      </span>

      <div className="purchase__line-footer">
        <span className="purchase__line-summary">
          <span className="purchase__qty">
            {num(line.received_quantity)} / {num(line.quantity)}
            {left > 0 && Number(line.received_quantity) > 0 && (
              <em className="stock__when"> {t('purchases.left', { n: num(left) })}</em>
            )}
          </span>

          {/* Pointing at the figure asks "is that a good price?", and the answer —
              what it cost before, what it averages — is otherwise a screen away. */}
          <PriceHint componentId={line.component_id}>
            <span className="purchase__price">
              {sum !== null && (
                <strong>
                  {num(sum.toFixed(4))} {currency}
                </strong>
              )}
              {/* The sum with the arithmetic that produced it, in brackets. Written
                  as "238 RUB × 23.8" it read as the total multiplied by something —
                  the quantity was missing, so the only two numbers on the line
                  looked like the two sides of the multiplication. */}
              {line.unit_price && (
                <em className="stock__when">
                  {' '}
                  ({num(line.quantity)} × {num(line.unit_price)})
                </em>
              )}
            </span>
          </PriceHint>
        </span>

        {editable && (
          <span className="purchase__line-actions">
            <button
              type="button"
              className="icon-button"
              title={t('components.edit')}
              aria-label={t('components.edit')}
              onClick={() => setEditing(true)}
            >
              <EditIcon />
            </button>
          {/* Absent once anything has been received: the receipts point at this
            line, and the ledger they live in is append-only. */}
          {Number(line.received_quantity) === 0 && (
            <button type="button" onClick={onDelete}>
              ✕
            </button>
          )}
          </span>
        )}
      </div>
    </li>
  );
}

function CoverageEditor({ purchaseId, line, onChanged }: { purchaseId: string; line: PurchaseLine; onChanged: () => void }) {
  const { t } = useTranslation();
  const projects = useQuery({ queryKey: ['projects', 'list', 'coverage'], queryFn: () => listProjects() });
  const [projectId, setProjectId] = useState('');
  const [quantity, setQuantity] = useState('');
  const save = useMutation({
    mutationFn: (allocations: Array<{ project_id: string; quantity: string }>) => setLineCoverage(purchaseId, line.id, allocations),
    onSuccess: onChanged,
  });
  const remaining = Math.max(0, outstanding(line) - line.coverage.reduce((sum, item) => sum + Number(item.quantity), 0));
  return (
    <details className="purchase__coverage">
      <summary>{t('purchases.coverage')} ({num(line.coverage.reduce((sum, item) => sum + Number(item.quantity), 0))})</summary>
      {line.coverage.map((item) => <span key={item.id}>{projects.data?.find((project) => project.id === item.project_id)?.code ?? item.project_id}: {num(item.quantity)} </span>)}
      <div>
        <select value={projectId} onChange={(event) => setProjectId(event.target.value)}><option value="">{t('purchases.project')}</option>{(projects.data ?? []).map((project) => <option key={project.id} value={project.id}>{project.code}</option>)}</select>
        <input value={quantity} onChange={(event) => setQuantity(event.target.value)} placeholder={t('purchases.quantity')} inputMode="decimal" />
        <button type="button" disabled={!projectId || !quantity || Number(quantity) <= 0 || Number(quantity) > remaining || save.isPending} onClick={() => save.mutate([...line.coverage.map((item) => ({ project_id: item.project_id, quantity: item.quantity })), { project_id: projectId, quantity }])}>{t('purchases.assignProject')}</button>
        {line.coverage.length > 0 && <button type="button" disabled={save.isPending} onClick={() => save.mutate([])}>{t('purchases.clearCoverage')}</button>}
      </div>
    </details>
  );
}

function AddLineForm({
  busy,
  onAdd,
}: {
  busy: boolean;
  onAdd: (input: Parameters<typeof addLine>[1]) => void;
}) {
  const { t } = useTranslation();
  const [component, setComponent] = useState<ComponentBrief | null>(null);
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [amount, setAmount] = useState('');
  const [target, setTarget] = useState<string | null>(null);
  const [purpose, setPurpose] = useState('');
  const [expectedAt, setExpectedAt] = useState('');
  const [lotNumber, setLotNumber] = useState('');
  const [dateCode, setDateCode] = useState('');
  const [packageType, setPackageType] = useState('');
  const [tare, setTare] = useState('');
  const [lotNote, setLotNote] = useState('');
  const [pickerKey, setPickerKey] = useState(0);

  return (
    <form
      className="purchase__add"
      onSubmit={(event) => {
        event.preventDefault();
        if (!component || !quantity) return;
        onAdd({
          component_id: component.id,
          quantity,
          unit_price: price || null,
          target_location_id: target,
          expected_at: expectedAt || null,
          purpose_note: purpose || null,
          receipt_lot_number: lotNumber.trim() || null,
          receipt_date_code: dateCode.trim() || null,
          receipt_package_type: packageType.trim() || null,
          receipt_tare_weight_g: tare.trim() || null,
          receipt_lot_note: lotNote.trim() || null,
        });
        setComponent(null);
        setQuantity('');
        setPrice('');
        setAmount('');
        setTarget(null);
        setExpectedAt('');
        setLotNumber('');
        setDateCode('');
        setPackageType('');
        setTare('');
        setLotNote('');
        setPickerKey((key) => key + 1);
        // "What for" is deliberately kept: an order is almost always for one
        // project, and retyping it on every line is how it ends up blank.
      }}
    >
      <ComponentPicker
        key={`component-${pickerKey}`}
        label={t('purchases.component')}
        value={component}
        onChange={setComponent}
      />
      <input
        value={quantity}
        onChange={(event) => {
          const next = event.target.value;
          setQuantity(next);
          // The shop states either a unit price or a line total; whichever was
          // typed stays put, and the other follows the quantity.
          if (price) setAmount(amountOf(next, price));
          else if (amount) setPrice(priceOf(next, amount));
        }}
        placeholder={t('purchases.quantity')}
        inputMode="decimal"
        size={6}
        required
      />
      {/* Amount first: it is what the invoice states and what people copy;
          the unit price is the figure that follows from it. */}
      <input
        value={amount}
        onChange={(event) => {
          setAmount(event.target.value);
          setPrice(priceOf(quantity, event.target.value));
        }}
        placeholder={t('purchases.amount')}
        inputMode="decimal"
        size={8}
      />
      <input
        value={price}
        onChange={(event) => {
          setPrice(event.target.value);
          setAmount(amountOf(quantity, event.target.value));
        }}
        placeholder={t('purchases.price')}
        inputMode="decimal"
        size={7}
      />
      {/* A suggestion for where it will land, not a rule — the parcel is
          unpacked wherever there is room, and receiving asks again. The places
          this part has lived in are offered first. */}
      <LocationPicker
        key={`location-${pickerKey}`}
        label={t('purchases.targetLocation')}
        value={target}
        onChange={setTarget}
        allowOutside={false}
        componentId={component?.id ?? null}
      />
      <input
        value={purpose}
        onChange={(event) => setPurpose(event.target.value)}
        placeholder={t('purchases.purpose')}
      />
      <input
        type="date"
        value={expectedAt}
        onChange={(event) => setExpectedAt(event.target.value)}
        aria-label={t('purchases.expectedAt')}
      />
      <ReceiptDefaultsFields
        lotNumber={lotNumber}
        dateCode={dateCode}
        packageType={packageType}
        tare={tare}
        note={lotNote}
        onLotNumber={setLotNumber}
        onDateCode={setDateCode}
        onPackageType={setPackageType}
        onTare={setTare}
        onNote={setLotNote}
      />
      <button type="submit" disabled={busy || !component}>
        {t('purchases.addLine')}
      </button>
    </form>
  );
}

/** Defaults are recorded with the order line and copied into a future receipt.
 * Keeping the five fields together makes the everyday order row stay readable,
 * while the supplier's reel information is still one click away when known. */
function ReceiptDefaultsFields({
  lotNumber,
  dateCode,
  packageType,
  tare,
  note,
  onLotNumber,
  onDateCode,
  onPackageType,
  onTare,
  onNote,
  initiallyOpen = false,
}: {
  lotNumber: string;
  dateCode: string;
  packageType: string;
  tare: string;
  note: string;
  onLotNumber: (value: string) => void;
  onDateCode: (value: string) => void;
  onPackageType: (value: string) => void;
  onTare: (value: string) => void;
  onNote: (value: string) => void;
  initiallyOpen?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <details className="purchase__receipt-defaults" open={initiallyOpen}>
      <summary>{t('purchases.receiptDefaults')}</summary>
      <div className="purchase__lot-fields">
        <label>
          {t('lots.externalLot')}
          <SupplierLotInput value={lotNumber} onChange={onLotNumber} />
        </label>
        <label>
          {t('lots.dateCode')}
          <input value={dateCode} onChange={(event) => onDateCode(event.target.value)} />
        </label>
        <label>
          {t('lots.packageType')}
          <PackageTypeInput value={packageType} onChange={onPackageType} />
        </label>
        <label>
          {t('lots.tareInput')}
          <input value={tare} onChange={(event) => onTare(event.target.value)} inputMode="decimal" />
        </label>
        <label className="purchase__lot-note">
          {t('purchases.lotNote')}
          <input value={note} onChange={(event) => onNote(event.target.value)} />
        </label>
      </div>
    </details>
  );
}

function PackageTypeInput({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const kinds = useQuery({ queryKey: ['stock', 'package-types'], queryFn: listPackageTypes });
  const listId = useId();
  return <>
    <input list={listId} value={value} onChange={(event) => onChange(event.target.value)} />
    <datalist id={listId}>
      {(kinds.data ?? []).map((kind) => <option key={kind} value={kind} />)}
    </datalist>
  </>;
}

/** A supplier barcode is data, unless it resolves to one of our own labels.
 * Keyboard-wedge readers type directly into the input; port readers arrive via
 * the scan channel. Both routes therefore make the same explicit decision. */
function SupplierLotInput({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const { t } = useTranslation();
  const [active, setActive] = useState(false);
  const [internal, setInternal] = useState(false);

  const accept = (raw: string) => {
    const code = raw.trim();
    if (!code) return;
    void resolveScan(code)
      .then((result) => {
        if (result.resolved) {
          // An application QR belongs to its own workflow; it must never turn
          // into a supplier lot merely because this field had focus.
          onChange('');
          setInternal(true);
        } else {
          onChange(code);
          setInternal(false);
        }
      })
      .catch(() => {
        // A scanner must remain useful during a temporary network failure.
        onChange(code);
        setInternal(false);
      });
  };

  useScanTarget(accept, active);

  return <>
    <input
      value={value}
      onChange={(event) => { onChange(event.target.value); setInternal(false); }}
      onFocus={() => setActive(true)}
      onBlur={() => setActive(false)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          accept(event.currentTarget.value);
        }
      }}
    />
    {internal && <span className="hint">{t('purchases.supplierLotInternal')}</span>}
  </>;
}

/**
 * What actually arrived, and where it went.
 *
 * Every line is offered with what is still outstanding pre-filled, so the common
 * case — the whole parcel came — is one press. Lines left at zero are simply not
 * sent: "did not arrive" and "arrived as nothing" are different facts.
 */
function ReceiveForm({
  lines,
  busy,
  onSubmit,
  onCancel,
}: {
  lines: PurchaseLine[];
  busy: boolean;
  onSubmit: (input: ReceiveRequest) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  // `toISOString()` alone names the previous day west of UTC. The input is a
  // calendar date, so derive it from the browser's local clock instead.
  const [receivedAt, setReceivedAt] = useState(() => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 10);
  });
  const [quantities, setQuantities] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      lines.map((line) => [line.id, outstanding(line) > 0 ? num(outstanding(line)) : '']),
    ),
  );
  const [places, setPlaces] = useState<Record<string, string | null>>(() =>
    Object.fromEntries(lines.map((line) => [line.id, line.target_location_id])),
  );
  const [serials, setSerials] = useState<Record<string, string>>({});
  const [lotNumbers, setLotNumbers] = useState<Record<string, string>>(() =>
    Object.fromEntries(lines.map((line) => [line.id, line.receipt_lot_number ?? ''])),
  );
  const [dateCodes, setDateCodes] = useState<Record<string, string>>(() =>
    Object.fromEntries(lines.map((line) => [line.id, line.receipt_date_code ?? ''])),
  );
  const [packageTypes, setPackageTypes] = useState<Record<string, string>>(() =>
    Object.fromEntries(lines.map((line) => [line.id, line.receipt_package_type ?? ''])),
  );
  const [tares, setTares] = useState<Record<string, string>>(() =>
    Object.fromEntries(lines.map((line) => [line.id, line.receipt_tare_weight_g ?? ''])),
  );
  const [lotNotes, setLotNotes] = useState<Record<string, string>>(() =>
    Object.fromEntries(lines.map((line) => [line.id, line.receipt_lot_note ?? ''])),
  );
  const [scanning, setScanning] = useState<string | null>(null);
  // Which line a scanned shelf code belongs to. A parcel with six lines in it is
  // put away one line at a time, and the reader has no idea which — so the line
  // last touched is the one that claims the next reading. The first line is the
  // opening guess, which is right whenever there is only one.
  const [activeLine, setActiveLine] = useState<string | null>(lines[0]?.id ?? null);
  // Checked once: on a desktop without a camera the button would only ever
  // open a dialog that says it cannot help.
  const cameraAvailable = scanner.cameraSupport() === 'ok';

  // One per line, blanks kept: a blank slot is a unit that arrived without a
  // legible sticker, and dropping it would quietly change how many arrived.
  const enteredSerials = (lineId: string): string[] => {
    const raw = serials[lineId] ?? '';
    if (!raw.trim()) return [];
    return raw.split('\n').map((value) => value.trim());
  };

  const serialsWrong = (line: PurchaseLine): boolean => {
    if (!line.component?.track_serials) return false;
    const entered = enteredSerials(line.id);
    return entered.length > 0 && entered.length !== Number(quantities[line.id] ?? 0);
  };

  const ready: ReceiptLine[] = lines.flatMap((line) => {
    const quantity = quantities[line.id] ?? '';
    const place = places[line.id] ?? null;
    const lotNumber = lotNumbers[line.id]?.trim() ?? '';
    const dateCode = dateCodes[line.id]?.trim() ?? '';
    const tare = tares[line.id]?.trim() ?? '';
    const lotNote = lotNotes[line.id]?.trim() ?? '';
    if (!(Number(quantity) > 0) || !place || serialsWrong(line)) return [];
    return [
      {
        line_id: line.id,
        quantity,
        location_id: place,
        ...(line.component?.track_serials ? { serials: enteredSerials(line.id) } : {}),
        ...(lotNumber ? { external_lot_number: lotNumber } : {}),
        ...(dateCode ? { date_code: dateCode } : {}),
        ...(lotNote ? { lot_note: lotNote } : {}),
        packages: [{
          quantity,
          package_type: packageTypes[line.id]?.trim() || 'container',
          ...(tare ? { tare_weight_g: tare } : {}),
        }],
      },
    ];
  });
  const missingPlace = lines.some(
    (line) => Number(quantities[line.id] ?? 0) > 0 && !places[line.id],
  );
  const badSerials = lines.some((line) => Number(quantities[line.id] ?? 0) > 0 && serialsWrong(line));

  return (
    <form
      className="purchase__receive"
      onSubmit={(event) => {
        event.preventDefault();
        if (ready.length === 0) return;
        // Noon is intentional: it preserves the selected date for the server
        // even when its clock is in another timezone.
        onSubmit({ lines: ready, occurred_at: `${receivedAt}T12:00:00` });
      }}
      onKeyDown={(event) => {
        // Enter from a supplier-lot field used to submit the whole form before
        // the operator had finished choosing the actual shelf.  Receiving is a
        // stock movement, so it now requires the explicit action below.  The
        // location picker still handles a scanner's Enter itself.
        const tag = (event.target as HTMLElement).tagName;
        if (event.key === 'Enter' && tag !== 'TEXTAREA' && tag !== 'BUTTON') {
          event.preventDefault();
        }
      }}
    >
      <label className="purchase__receive-date">
        {t('purchases.receivedAt')}
        <input
          type="date"
          value={receivedAt}
          onChange={(event) => setReceivedAt(event.target.value)}
          required
        />
      </label>
      <ul className="purchase__lines">
        {lines.map((line) => (
          <li
            key={line.id}
            className={
              line.id === activeLine ? 'purchase__line purchase__line--active' : 'purchase__line'
            }
            onFocusCapture={() => setActiveLine(line.id)}
          >
            <span className="purchase__line-name">
              <span className="components__name">{line.component?.name}</span>
              <span className="components__meta">
                <em>{t('purchases.ordered', { n: num(line.quantity) })}</em>
                <em>{t('purchases.alreadyIn', { n: num(line.received_quantity) })}</em>
              </span>
            </span>
            <div className="purchase__receive-main">
              <label>
                {t('purchases.quantity')}
                <input
                  value={quantities[line.id] ?? ''}
                  onChange={(event) =>
                    setQuantities((current) => ({ ...current, [line.id]: event.target.value }))
                  }
                  inputMode="decimal"
                  size={6}
                />
              </label>
              <label>
                {t('purchases.place')}
                <LocationPicker
                  label={t('purchases.place')}
                  value={places[line.id] ?? null}
                  onChange={(id) => setPlaces((current) => ({ ...current, [line.id]: id }))}
                  allowOutside={false}
                  componentId={line.component_id}
                  scanTarget={line.id === activeLine}
                  onFocus={() => setActiveLine(line.id)}
                />
              </label>
            </div>
            <details
              className="purchase__receipt-details"
              open={Boolean(
                line.receipt_lot_number || line.receipt_date_code || line.receipt_package_type ||
                line.receipt_tare_weight_g || line.receipt_lot_note,
              )}
            >
              <summary>{t('purchases.receiptDetails')}</summary>
              <div className="purchase__lot-fields">
                <label>
                  {t('lots.externalLot')}
                  <SupplierLotInput
                    value={lotNumbers[line.id] ?? ''}
                    onChange={(value) => setLotNumbers((current) => ({ ...current, [line.id]: value }))}
                  />
                </label>
                <label>
                  {t('lots.dateCode')}
                  <input value={dateCodes[line.id] ?? ''} onChange={(event) => setDateCodes((current) => ({ ...current, [line.id]: event.target.value }))} />
                </label>
                <label>
                  {t('lots.packageType')}
                  <PackageTypeInput
                    value={packageTypes[line.id] ?? ''}
                    onChange={(value) => setPackageTypes((current) => ({ ...current, [line.id]: value }))}
                  />
                </label>
                <label>
                  {t('lots.tareInput')}
                  <input value={tares[line.id] ?? ''} onChange={(event) => setTares((current) => ({ ...current, [line.id]: event.target.value }))} inputMode="decimal" />
                </label>
                <label className="purchase__lot-note">
                  {t('purchases.lotNote')}
                  <input value={lotNotes[line.id] ?? ''} onChange={(event) => setLotNotes((current) => ({ ...current, [line.id]: event.target.value }))} />
                </label>
              </div>
            </details>

            {/* Only for parts tracked unit by unit, and only while something is
                actually arriving on this line: the box is where the numbers get
                copied off the units as they come out of the parcel. */}
            {line.component?.track_serials && Number(quantities[line.id] ?? 0) > 0 && (
              <div className="purchase__serials">
                <label htmlFor={`serials-${line.id}`}>{t('purchases.serials')}</label>
                <textarea
                  id={`serials-${line.id}`}
                  rows={Math.min(6, Math.max(2, Number(quantities[line.id] ?? 0)))}
                  value={serials[line.id] ?? ''}
                  onChange={(event) =>
                    setSerials((current) => ({ ...current, [line.id]: event.target.value }))
                  }
                  placeholder={t('purchases.serialsPlaceholder')}
                />
                <div className="purchase__serials-actions">
                  <p className="panel__hint">
                    {enteredSerials(line.id).length === 0
                      ? t('purchases.serialsOurs', { n: num(quantities[line.id] ?? 0) })
                      : t('purchases.serialsCount', {
                          entered: enteredSerials(line.id).length,
                          expected: num(quantities[line.id] ?? 0),
                        })}
                  </p>
                  {cameraAvailable && (
                    <button type="button" onClick={() => setScanning(line.id)}>
                      {t('serials.scan.open')}
                    </button>
                  )}
                </div>

                {scanning === line.id && (
                  <SerialScanner
                    componentId={line.component_id}
                    expected={Number(quantities[line.id] ?? 0) || undefined}
                    initial={enteredSerials(line.id)}
                    onCancel={() => setScanning(null)}
                    onDone={(scanned) => {
                      setSerials((current) => ({ ...current, [line.id]: scanned.join('\n') }));
                      setScanning(null);
                    }}
                  />
                )}
              </div>
            )}
          </li>
        ))}
      </ul>

      {missingPlace && <p className="panel__hint">{t('purchases.needPlace')}</p>}
      {badSerials && <p className="panel__hint">{t('purchases.serialsMismatch')}</p>}

      <div className="component-form__actions">
        <button type="submit" disabled={busy || ready.length === 0}>
          {t('purchases.receive')}
        </button>
        <button type="button" onClick={onCancel}>
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}
